import React, { useEffect } from 'react';
import { useStore } from '../../../../../../stores/root';
import { NavLink } from 'react-router-dom';

// assets
import { faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ErrorService from '../../../../../../services/general/error';
import ShipSchedulesService from '../../../../../../services/data/ships/shipSchedules';

// props
import { IShipScheduleResourceShort } from '../../../../../../props/data/ships';
import { TTagVariant } from '../../../../../uiframeworks/tag';

// components
import { ActionButton, CommandBar, Dropdown, IDropdownOption, IColumn, ICommandBarItemProps, IContextualMenuItem, PrimaryButton, SearchBox, SelectionMode, ShimmeredDetailsList, Stack } from '@fluentui/react';
import Text from './../../../../../typography/text';
import Tag from '../../../../../uiframeworks/tag';
import moment from 'moment';
import ShipScheduleForm from '../form';
import ShipScheduleStatusForm from '../status';
import PermissionsService from '../../../../../../services/permissions';
import NoAccess from '../../../../../uiframeworks/noAccess';

interface IShipSchedulesListProps {
    shipId: string;
}

const FILTER_OPTIONS: IDropdownOption[] = [
    { key: 'active', text: 'Jadwal Aktif & Mendatang' },
    { key: 'all', text: 'Semua Jadwal' },
    { key: 'past', text: 'Riwayat Selesai / Expired' },
];

const ShipSchedulesList: React.FC<IShipSchedulesListProps> = (props: IShipSchedulesListProps) => {
    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [showingMore, setShowingMore] = React.useState<boolean>(false);
    const [schedules, setSchedules] = React.useState<IShipScheduleResourceShort[]>([]);
    const [activeSchedule, setActiveSchedule] = React.useState<IShipScheduleResourceShort | undefined>();
    const [keyword, setKeyword] = React.useState<string>("");
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();
    const [filterMode, setFilterMode] = React.useState<'active' | 'all' | 'past'>('active');

    const hasPermission = PermissionsService.hasPermission(['ships.schedules.read.all'], user.permissions);

    const displayedSchedules = React.useMemo(() => {
        return schedules.filter(s => {
            const isPast = ShipSchedulesService.isExpired(s);
            if (filterMode === 'active') return !isPast;
            if (filterMode === 'past') return isPast;
            return true;
        });
    }, [schedules, filterMode]);

    const columns: IColumn[] = [
        {
            key: "voy",
            name: "Voy",
            fieldName: "voy",
            minWidth: 120,
            maxWidth: 160,
            onRender: (item: IShipScheduleResourceShort) => {
                const isPast = ShipSchedulesService.isExpired(item);
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <NavLink to={`/operationals/ships/${item.id}/schedules/${item.id}`}>
                        <Text style={{ fontWeight: 600 }}>{item.voy}</Text>
                    </NavLink>
                    {isPast ? (
                        <span style={{ fontSize: 9, color: '#8c8c8c', fontStyle: 'italic' }}>Expired / Past</span>
                    ) : null}
                </Stack>
            }
        },
        {
            key: "actions",
            name: "",
            minWidth: 50,
            maxWidth: 50,
            onRender: (item: IShipScheduleResourceShort) => {
                let menuItems: IContextualMenuItem[] = []

                if (PermissionsService.hasPermission(['ships.schedules.update.all'], user.permissions)) {
                    menuItems.push({
                        key: "updateDetails", text: "Update details", iconProps: { iconName: "Edit" }, onClick: () => {
                            setActiveSchedule(item);
                            setActiveSurface('update');
                        }
                    });
                }

                if (PermissionsService.hasPermission(['ships.schedules.update.all'], user.permissions)) {
                    menuItems.push({
                        key: "updateStatus", text: "Update status", iconProps: { iconName: "CompositeCheckbox" }, onClick: () => {
                            setActiveSchedule(item);
                            setActiveSurface('updateStatus');
                        }
                    });
                }

                return <Stack.Item styles={{ root: { padding: '4px 0px' } }}>
                    {menuItems.length > 0 ? <Stack.Item className={"detailsListActionRow"}>
                        <ActionButton className={'detailsListActionButton'} menuProps={{
                            items: menuItems
                        }} />
                    </Stack.Item> : null}
                </Stack.Item>;
            }
        },
        {
            key: 'status',
            fieldName: 'status',
            name: 'Status',
            minWidth: 100,
            maxWidth: 100,
            onRender: (item: IShipScheduleResourceShort) => {
                let statusVariant: TTagVariant = 'default';
                let statusText: string = 'Scheduled';

                if (item.status === 'sailing') {
                    statusText = 'Sailing';
                    statusVariant = 'warning';
                } else if (item.status === 'arrived') {
                    statusText = 'Docked';
                    statusVariant = 'success';
                } else if (item.status === 'cancelled') {
                    statusText = 'Cancelled';
                    statusVariant = 'error';
                }

                return (
                    <Stack horizontal>
                        <Tag text={statusText} variant={statusVariant} />
                    </Stack>
                );
            }
        },
        {
            key: "portOfLoading",
            name: "Port of Loading (POL)",
            minWidth: 260,
            isMultiline: true,
            onRender: (item: IShipScheduleResourceShort) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <Text>{item.originAddress}</Text>
                </Stack>
            }
        },
        {
            key: "portOfDeparture",
            name: "Port of Discharge (POD)",
            minWidth: 260,
            isMultiline: true,
            onRender: (item: IShipScheduleResourceShort) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <Text>{item.destinationAddress}</Text>
                </Stack>
            }
        },
        {
            key: "departureDate",
            name: "Departure Date",
            minWidth: 180,
            maxWidth: 180,
            isMultiline: true,
            onRender: (item: IShipScheduleResourceShort) => {
                return <Stack horizontal tokens={{ childrenGap: 5 }} styles={{ root: { padding: '4px 0px' } }}>
                    {item.actualDeparture ? <>
                        <Text>{moment(item.actualDeparture).format("DD/MM/YYYY")}</Text>
                        <Text style={{ fontStyle: 'italic' }}>(Actual)</Text>
                    </> : null}
                    {!item.actualDeparture ? <>
                        <Text>{moment(item.estimatedDeparture).format("DD/MM/YYYY")}</Text>
                        <Text style={{ fontStyle: 'italic' }}>(Estimated)</Text>
                    </> : null}
                </Stack>
            }
        },
        {
            key: "arrivalDate",
            name: "Arrival Date",
            minWidth: 180,
            maxWidth: 180,
            isMultiline: true,
            onRender: (item: IShipScheduleResourceShort) => {
                return <Stack horizontal tokens={{ childrenGap: 5 }} styles={{ root: { padding: '4px 0px' } }}>
                    {item.actualArrival ? <>
                        <Text>{moment(item.actualArrival).format("DD/MM/YYYY")}</Text>
                        <Text style={{ fontStyle: 'italic' }}>(Actual)</Text>
                    </> : null}
                    {!item.actualArrival ? <>
                        <Text>{moment(item.estimatedArrival).format("DD/MM/YYYY")}</Text>
                        <Text style={{ fontStyle: 'italic' }}>(Estimated)</Text>
                    </> : null}
                </Stack>
            }
        }
    ];

    useEffect(() => {
        _onRetrieveSchedules();
    }, [keyword]);

    const _onRetrieveSchedules = async (pageNumber?: number,) => {
        try {
            if (!pageNumber) {
                setLoaded(false);
            }

            const qs: string[] = [];
            qs.push(`ship_id=${props.shipId}`);
            if (pageNumber) { qs.push(`page=${pageNumber}`); }
            if (keyword && keyword.trim() !== "") { qs.push(`search=${keyword}`) }

            const results = await ShipSchedulesService.retrieve(qs.join("&"));
            setSchedules(results);
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'retrieve_ship_schedules_error',
                text: `Failed to retrieve ship's schedules list. Error: ` + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
        }
    }

    const _onKeywordChanged = (value?: string) => {
        setLoaded(true);
        setKeyword(value || "");
    }

    const getCommandBarItems = () => {
        let items: ICommandBarItemProps[] = [];
        let farItems: ICommandBarItemProps[] = [];

        items.push({
            key: "search",
            onRender: () => {
                return <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="center">
                    <SearchBox placeholder={"Search ..."} onSearch={_onKeywordChanged} />
                    <Dropdown
                        options={FILTER_OPTIONS}
                        selectedKey={filterMode}
                        onChange={(_, opt) => {
                            if (opt) setFilterMode(opt.key as 'active' | 'all' | 'past');
                        }}
                        styles={{ root: { width: 190 } }}
                    />
                </Stack>
            }
        });

        if (PermissionsService.hasPermission(['ships.schedules.create'], user.permissions)) {
            farItems.push({
                key: "register",
                text: "Create Schedule",
                iconProps: { iconName: "Add" },
                onRender: () => {
                    return <PrimaryButton text={"Create Schedule"}
                        iconProps={{ iconName: "Add" }}
                        onClick={() => { setActiveSurface('create') }}
                        styles={{ root: { marginLeft: 10 } }} />;
                }
            });
        }

        return { items, farItems };
    }

    const _onSurfaceDismissed = (refresh?: boolean) => {
        setActiveSurface(undefined);
        setActiveSchedule(undefined);

        if (refresh) { _onRetrieveSchedules() }
    }

    return <Stack tokens={{ childrenGap: 20 }} styles={{ root: { marginTop: 10 } }}>
        {hasPermission ? <>
            <CommandBar
                items={getCommandBarItems().items}
                farItems={getCommandBarItems().farItems}
                styles={{
                    root: {
                        padding: 0,
                        height: 'unset',
                        backgroundColor: 'transparent'
                    }
                }}
                ariaLabel="Use left and right arrow keys to navigate between commands" />
            <Stack>
                {
                    !loaded || (loaded && displayedSchedules.length > 0) ? (
                        <>
                            <ShimmeredDetailsList
                                setKey="items"
                                items={displayedSchedules}
                                columns={columns}
                                selectionMode={SelectionMode.none}
                                enableShimmer={!loaded}
                                onShouldVirtualize={() => false}
                                ariaLabelForShimmer="Content is being fetched"
                                ariaLabelForGrid="Item details" />
                        </>
                    ) : null
                }
                {loaded && displayedSchedules.length < 1 ? <Text>Ship schedule(s) not found</Text> : null}
            </Stack>
            {activeSurface === 'create' ? <ShipScheduleForm shipId={props.shipId} onDismissed={_onSurfaceDismissed} /> : null}
            {activeSurface === 'update' && activeSchedule ? <ShipScheduleForm shipId={props.shipId} scheduleId={activeSchedule.id} onDismissed={_onSurfaceDismissed} /> : null}
            {activeSurface === 'updateStatus' && activeSchedule ? <ShipScheduleStatusForm scheduleId={activeSchedule.id} onDismissed={_onSurfaceDismissed} /> : null}
        </> : null}
        {!hasPermission ? <NoAccess /> : null}
    </Stack>;
};

export default ShipSchedulesList;